import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import BackgroundVideoStrip from "@/components/BackgroundVideoStrip";
import { BG_VIDEOS } from "@/data/backgroundVideos";
import {
  ArrowRight,
  Zap,
  Users,
  Building2,
  Bot,
  Play,
  Star,
  ChevronRight,
  ChevronLeft,
  Sparkles,
  Award,
  Megaphone,
  Calendar,
  MessageSquare,
  TrendingUp,
  Shield,
  Tv,
  Radio,
  Globe,
  DollarSign,
  CheckCircle2,
  Phone,
  FileText,
  BarChart3,
  CreditCard,
  Rocket,
  Brain,
  Settings,
  Target,
  Lock,
  AlertTriangle,
  X,
  Search,
  Link2,
  MapPin,
  ExternalLink,
  Eye,
  Mail,
  Repeat,
  Monitor,
  Smartphone,
  Volume2,
  Send,
  MousePointer,
  Palette,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import ListingBenefitsSection from "@/components/ListingBenefitsSection";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

// AI Business Heroes Data
const aiBusinessHeroes = [
  {
    id: 1,
    name: "LEXI",
    role: "AI Marketing Hero",
    category: "Marketing & Traffic",
    description: "Deploys your brand across TV, radio, social, and ads. Publishes content and manages your reputation 24/7.",
    image: "https://customer-assets.emergentagent.com/job_ai-business-finder/artifacts/igr85vqt_ChatGPT%20Image%20Jan%2029%2C%202026%2C%2002_15_31%20PM.png",
    color: "#EC4899",
    capabilities: ["Social media management", "Content publishing", "Ad deployment", "Brand promotion"],
  },
  {
    id: 2,
    name: "ATLAS",
    role: "AI Lead Management Hero",
    category: "Leads & Sales",
    description: "Qualifies leads, manages your pipeline, and tracks every conversation to help close more deals.",
    image: "https://customer-assets.emergentagent.com/job_ai-business-finder/artifacts/kppw0exm_ChatGPT%20Image%20Jan%2029%2C%202026%2C%2002_24_54%20PM.png",
    color: "#00E5FF",
    capabilities: ["Lead qualification", "Pipeline management", "Conversation tracking", "Deal closing"],
  },
  {
    id: 3,
    name: "CALLIE",
    role: "AI Appointment Hero",
    category: "Communications",
    description: "Books appointments, confirms schedules, sends reminders, and syncs with your calendar automatically.",
    image: "https://customer-assets.emergentagent.com/job_ai-business-finder/artifacts/hc05rg8u_ChatGPT%20Image%20Jan%2029%2C%202026%2C%2002_28_26%20PM.png",
    color: "#F59E0B",
    capabilities: ["Appointment booking", "Calendar sync", "Reminders", "Schedule management"],
  },
  {
    id: 4,
    name: "EMBER",
    role: "AI Automations Hero",
    category: "Operations",
    description: "Manages workflows, automates tasks, and keeps your operations running smoothly around the clock.",
    image: "https://customer-assets.emergentagent.com/job_ai-business-finder/artifacts/912e00d4_ChatGPT%20Image%20Jan%2029%2C%202026%2C%2002_33_58%20PM.png",
    color: "#10B981",
    capabilities: ["Workflow automation", "Task management", "Process optimization", "Operations control"],
  },
  {
    id: 5,
    name: "REED",
    role: "AI Reporting Hero",
    category: "Data & Reporting",
    description: "Generates daily, weekly, monthly reports. Tracks ROI and performance to optimize your business automatically.",
    image: "https://customer-assets.emergentagent.com/job_ai-business-finder/artifacts/gyv10f2w_ChatGPT%20Image%20Jan%2029%2C%202026%2C%2002_36_35%20PM.png",
    color: "#14B8A6",
    capabilities: ["Performance tracking", "ROI analysis", "Automated reports", "Data visualization"],
  },
  {
    id: 6,
    name: "KAYA",
    role: "AI Billing Hero",
    category: "Payments & Finance",
    description: "Manages invoicing, payments, subscriptions, and keeps your financial operations running smoothly.",
    image: "https://customer-assets.emergentagent.com/job_ai-business-finder/artifacts/p9fes7dw_ChatGPT%20Image%20Jan%2029%2C%202026%2C%2003_02_50%20PM.png",
    color: "#8B5CF6",
    capabilities: ["Invoice management", "Payment processing", "Subscription handling", "Financial tracking"],
  },
  {
    id: 7,
    name: "LI",
    role: "AI Projects Hero",
    category: "Operations",
    description: "Manages projects, coordinates teams, and ensures on-time delivery for your business initiatives.",
    image: "https://customer-assets.emergentagent.com/job_ai-business-finder/artifacts/22hm5moj_ChatGPT%20Image%20Jan%2029%2C%202026%2C%2003_06_01%20PM.png",
    color: "#6366F1",
    capabilities: ["Project management", "Team coordination", "Timeline tracking", "Resource planning"],
  },
  {
    id: 8,
    name: "ZANE",
    role: "AI CRM Hero",
    category: "AI Horse-Powered CRM",
    description: "Manages your contacts, deals, pipeline, calendar, and all CRM operations powered by AI.",
    image: "https://customer-assets.emergentagent.com/job_ai-business-finder/artifacts/34mua9nn_ChatGPT%20Image%20Jan%2029%2C%202026%2C%2003_07_40%20PM.png",
    color: "#00E5FF",
    capabilities: ["Contact management", "Deal tracking", "Pipeline optimization", "AI-powered insights"],
  },
  {
    id: 9,
    name: "LAYLA",
    role: "AI Follow Up Hero",
    category: "Leads & Sales",
    description: "Never miss a follow-up again. Tracks missed calls, sends reminders, and prevents lost deals.",
    image: "https://customer-assets.emergentagent.com/job_76756fe8-afb0-475f-80d5-68ba3c26d4c4/artifacts/mg8h2yt1_ChatGPT%20Image%20Jan%2028%2C%202026%2C%2004_04_03%20PM.png",
    color: "#EF4444",
    capabilities: ["Follow-up automation", "Missed call tracking", "Deal recovery", "Reminder systems"],
  },
  {
    id: 10,
    name: "ARJUN",
    role: "AI Scheduling Hero",
    category: "Communications",
    description: "Manages and optimizes your entire schedule. Resolves conflicts and keeps your calendar organized automatically.",
    image: "https://customer-assets.emergentagent.com/job_76756fe8-afb0-475f-80d5-68ba3c26d4c4/artifacts/b4n2p2od_ChatGPT%20Image%20Jan%2028%2C%202026%2C%2004_05_17%20PM.png",
    color: "#F97316",
    capabilities: ["Smart scheduling", "Conflict resolution", "Calendar optimization", "Meeting coordination"],
  },
  {
    id: 11,
    name: "ANIKA",
    role: "AI Email Hero",
    category: "Communications",
    description: "Manages your inbox, prioritizes emails, drafts responses, and keeps your communication flowing.",
    image: "https://customer-assets.emergentagent.com/job_76756fe8-afb0-475f-80d5-68ba3c26d4c4/artifacts/j6e3a7uu_ChatGPT%20Image%20Jan%2028%2C%202026%2C%2004_07_36%20PM.png",
    color: "#3B82F6",
    capabilities: ["Email prioritization", "Inbox management", "Response drafting", "Communication flow"],
  },
  {
    id: 12,
    name: "RAY",
    role: "AI Writer Hero",
    category: "Marketing & Traffic",
    description: "Creates compelling content for social media, blogs, and marketing. Optimizes for readability and engagement.",
    image: "https://customer-assets.emergentagent.com/job_76756fe8-afb0-475f-80d5-68ba3c26d4c4/artifacts/8pi2egum_ChatGPT%20Image%20Jan%2028%2C%202026%2C%2004_09_23%20PM.png",
    color: "#A855F7",
    capabilities: ["Content creation", "Social media writing", "Blog posts", "Readability optimization"],
  },
  {
    id: 13,
    name: "STEPHANIE",
    role: "AI Pipeline Hero",
    category: "Leads & Sales",
    description: "Visualizes and manages your sales pipeline. Tracks prospects, meetings, and won deals in real-time.",
    image: "https://customer-assets.emergentagent.com/job_76756fe8-afb0-475f-80d5-68ba3c26d4c4/artifacts/m2hnc8tk_ChatGPT%20Image%20Jan%2028%2C%202026%2C%2004_18_21%20PM.png",
    color: "#EF4444",
    capabilities: ["Pipeline visualization", "Deal tracking", "Prospect management", "Sales analytics"],
  },
  {
    id: 14,
    name: "DAISY",
    role: "AI Directory Hero",
    category: "Directory & Network",
    description: "Your AI Directory Assistant. Helps discover top talent, navigate listings, and connect with the best businesses.",
    image: "https://customer-assets.emergentagent.com/job_76756fe8-afb0-475f-80d5-68ba3c26d4c4/artifacts/ax9b6bmu_ChatGPT%20Image%20Jan%2028%2C%202026%2C%2004_39_31%20PM.png",
    color: "#F59E0B",
    capabilities: ["Directory navigation", "Talent discovery", "Business matching", "Best of Best curation"],
  },
  {
    id: 15,
    name: "MAVS",
    role: "AI Prospecting Hero",
    category: "Leads & Sales",
    description: "Identifies and qualifies top prospects. Analyzes data to find ideal leads and rates them for maximum conversion.",
    image: "https://customer-assets.emergentagent.com/job_4f66d136-c44f-4d88-a347-fb7cf990046b/artifacts/ofc5ri1i_ChatGPT%20Image%20Jan%2028%2C%202026%2C%2004_44_36%20PM.png",
    color: "#EF4444",
    capabilities: ["Prospect identification", "Lead scoring", "Data analysis", "Conversion optimization"],
  },
  {
    id: 16,
    name: "SARAH",
    role: "AI Reviews Hero",
    category: "Reputation & Community",
    description: "Manages your online reviews and reputation. Collects 5-star feedback and showcases your best testimonials.",
    image: "https://customer-assets.emergentagent.com/job_4f66d136-c44f-4d88-a347-fb7cf990046b/artifacts/rts6hr1z_ChatGPT%20Image%20Jan%2028%2C%202026%2C%2004_46_45%20PM.png",
    color: "#F59E0B",
    capabilities: ["Review management", "Reputation monitoring", "Testimonial collection", "Star rating optimization"],
  },
  {
    id: 17,
    name: "CHAD",
    role: "AI Spokesperson Hero",
    category: "Directory & Network",
    description: "Your brand's AI voice. Presents your business to the world and promotes your top offerings across channels.",
    image: "https://customer-assets.emergentagent.com/job_4f66d136-c44f-4d88-a347-fb7cf990046b/artifacts/dufxsy6a_ChatGPT%20Image%20Jan%2028%2C%202026%2C%2004_51_56%20PM.png",
    color: "#3B82F6",
    capabilities: ["Brand presentation", "Public speaking", "Business promotion", "Channel distribution"],
  },
  {
    id: 18,
    name: "JUSTIN",
    role: "AI Affiliate Hero",
    category: "Directory & Network",
    description: "Manages your affiliate network. Tracks referrals, builds partnerships, and drives revenue through collaboration.",
    image: "https://customer-assets.emergentagent.com/job_4f66d136-c44f-4d88-a347-fb7cf990046b/artifacts/200ldo83_ChatGPT%20Image%20Jan%2028%2C%202026%2C%2004_54_24%20PM.png",
    color: "#F97316",
    capabilities: ["Affiliate management", "Referral tracking", "Partnership building", "Revenue collaboration"],
  },
  {
    id: 19,
    name: "PRIYA",
    role: "AI Content Hero",
    category: "Marketing & Traffic",
    description: "Creates and distributes compelling content. Drives engagement through training, software insights, and coaching.",
    image: "https://customer-assets.emergentagent.com/job_4f66d136-c44f-4d88-a347-fb7cf990046b/artifacts/3joqda7j_ChatGPT%20Image%20Jan%2028%2C%202026%2C%2005_01_36%20PM.png",
    color: "#A855F7",
    capabilities: ["Content creation", "Training content", "Engagement driving", "Software insights"],
  },
  {
    id: 20,
    name: "CHANCE",
    role: "AI Funding Hero",
    category: "Payments & Finance",
    description: "Helps secure business funding and financial opportunities. Guides you through Co-Op funding and growth capital.",
    image: "https://customer-assets.emergentagent.com/job_4f66d136-c44f-4d88-a347-fb7cf990046b/artifacts/d907nn6c_ChatGPT%20Image%20Jan%2028%2C%202026%2C%2005_05_05%20PM.png",
    color: "#10B981",
    capabilities: ["Funding guidance", "Co-Op funding", "Financial planning", "Growth capital"],
  },
  {
    id: 21,
    name: "JULIE",
    role: "AI Course Creator Hero",
    category: "Marketing & Traffic",
    description: "Creates engaging training courses and educational content. Powers your knowledge base and team development.",
    image: "https://customer-assets.emergentagent.com/job_4f66d136-c44f-4d88-a347-fb7cf990046b/artifacts/58fuhm54_ChatGPT%20Image%20Jan%2028%2C%202026%2C%2005_24_59%20PM.png",
    color: "#F59E0B",
    capabilities: ["Course creation", "Training development", "Knowledge base", "Team education"],
  },
  {
    id: 22,
    name: "RYAN",
    role: "AI Recruiting Hero",
    category: "Human Capital",
    description: "Streamlines your hiring process. Reviews applicants, identifies top candidates, and manages recruitment pipeline.",
    image: "https://customer-assets.emergentagent.com/job_4f66d136-c44f-4d88-a347-fb7cf990046b/artifacts/inzqjre6_ChatGPT%20Image%20Jan%2028%2C%202026%2C%2005_19_21%20PM.png",
    color: "#3B82F6",
    capabilities: ["Applicant screening", "Candidate matching", "Recruitment pipeline", "Hiring automation"],
  },
  {
    id: 31,
    name: "KAITLYN",
    role: "AI Web Designer Hero",
    category: "Technology & Development",
    description: "Creates stunning, conversion-focused websites. Designs beautiful landing pages, sales funnels, and brand experiences that drive results.",
    image: "https://customer-assets.emergentagent.com/job_4f66d136-c44f-4d88-a347-fb7cf990046b/artifacts/wp4raehf_ChatGPT%20Image%20Jan%2028%2C%202026%2C%2010_53_38%20PM.png",
    color: "#EC4899",
    capabilities: ["Web design", "Landing pages", "Sales funnels", "Brand design", "UI/UX"],
  },
  {
    id: 32,
    name: "ARJUN",
    role: "AI App Developer Hero",
    category: "Technology & Development",
    description: "Builds powerful mobile and web applications. Turns your business ideas into reality with AI-powered development and automation.",
    image: "https://customer-assets.emergentagent.com/job_4f66d136-c44f-4d88-a347-fb7cf990046b/artifacts/buyc9tyh_ChatGPT%20Image%20Jan%2028%2C%202026%2C%2010_56_45%20PM.png",
    color: "#8B5CF6",
    capabilities: ["App development", "Custom software", "API integrations", "Automation", "AI solutions"],
  },
];

// Managed Ad Services Heroes - Managed Services covered with Co-Op Funding
const podAdsHeroes = [
  {
    id: 23,
    name: "ANGELA",
    role: "AI Meta Ad Hero",
    category: "Managed Ad Services",
    description: "Masters Facebook & Instagram advertising. Creates scroll-stopping ads that convert with AI-powered targeting and optimization.",
    image: "https://customer-assets.emergentagent.com/job_4f66d136-c44f-4d88-a347-fb7cf990046b/artifacts/53wmd6xr_ChatGPT%20Image%20Jan%2028%2C%202026%2C%2009_49_52%20PM.png",
    color: "#1877F2",
    capabilities: ["Facebook Ads", "Instagram Ads", "Audience targeting", "Ad optimization", "Retargeting"],
  },
  {
    id: 24,
    name: "LOGAN",
    role: "AI Google Ad Hero",
    category: "Managed Ad Services",
    description: "Dominates Google Search and Display. Puts your business at the top when customers are searching for your services.",
    image: "https://customer-assets.emergentagent.com/job_4f66d136-c44f-4d88-a347-fb7cf990046b/artifacts/3mj9m55a_ChatGPT%20Image%20Jan%2028%2C%202026%2C%2009_52_30%20PM.png",
    color: "#4285F4",
    capabilities: ["Search Ads", "Display Ads", "YouTube Ads", "Local Service Ads", "Performance Max"],
  },
  {
    id: 25,
    name: "ASHLEY",
    role: "AI TikTok Ad Hero",
    category: "Managed Ad Services",
    description: "Creates viral-worthy TikTok campaigns. Reaches younger audiences with engaging short-form video ads that drive action.",
    image: "https://customer-assets.emergentagent.com/job_4f66d136-c44f-4d88-a347-fb7cf990046b/artifacts/0190sdtb_ChatGPT%20Image%20Jan%2028%2C%202026%2C%2009_59_41%20PM.png",
    color: "#00F2EA",
    capabilities: ["TikTok Ads", "Video creation", "Viral campaigns", "Gen Z targeting", "Trend riding"],
  },
  {
    id: 26,
    name: "DEREK",
    role: "AI SEO & Ranking Hero",
    category: "Managed Ad Services",
    description: "Boosts your organic search rankings. Gets your business found on Google without paying for every click.",
    image: "https://customer-assets.emergentagent.com/job_4f66d136-c44f-4d88-a347-fb7cf990046b/artifacts/oyhcsmgy_ChatGPT%20Image%20Jan%2028%2C%202026%2C%2010_03_30%20PM.png",
    color: "#10B981",
    capabilities: ["On-page SEO", "Local SEO", "Content optimization", "Link building", "Rank tracking"],
  },
  {
    id: 27,
    name: "MARCUS",
    role: "AI Direct Mail Hero",
    category: "Managed Ad Services",
    description: "Delivers high-impact direct mail campaigns. Reaches customers in their mailbox with personalized, AI-optimized mailers.",
    image: "https://customer-assets.emergentagent.com/job_4f66d136-c44f-4d88-a347-fb7cf990046b/artifacts/acz2nyhq_ChatGPT%20Image%20Jan%2028%2C%202026%2C%2010_19_28%20PM.png",
    color: "#F59E0B",
    capabilities: ["Direct mail design", "Targeted mailing lists", "EDDM campaigns", "Postcard marketing", "Response tracking"],
  },
  {
    id: 28,
    name: "JAMES",
    role: "AI TV Ad Hero",
    category: "Managed Ad Services",
    description: "Places your business on 500+ TV stations. Retargets audiences across streaming and broadcast with AI-powered precision.",
    image: "https://customer-assets.emergentagent.com/job_4f66d136-c44f-4d88-a347-fb7cf990046b/artifacts/v2f8s9jq_ChatGPT%20Image%20Jan%2028%2C%202026%2C%2010_21_51%20PM.png",
    color: "#EF4444",
    capabilities: ["TV Advertising", "500+ Stations", "Streaming Ads", "Retargeting audiences", "Brand awareness"],
  },
  {
    id: 29,
    name: "DANIELLE",
    role: "AI Radio Ad Hero",
    category: "Managed Ad Services",
    description: "Broadcasts your message across 1000+ radio stations. AI-targeted radio campaigns that reach the right listeners.",
    image: "https://customer-assets.emergentagent.com/job_4f66d136-c44f-4d88-a347-fb7cf990046b/artifacts/frlt1sun_ChatGPT%20Image%20Jan%2028%2C%202026%2C%2010_23_55%20PM.png",
    color: "#A855F7",
    capabilities: ["Radio Advertising", "1000+ Stations", "AI Targeting", "Audio campaigns", "Drive-time slots"],
  },
  {
    id: 30,
    name: "BLAKE",
    role: "AI Cinematic Ad Hero",
    category: "Managed Ad Services",
    description: "Creates Hollywood-quality cinematic video ads. AI-powered scripts, stunning visuals, and professional production.",
    image: "https://customer-assets.emergentagent.com/job_4f66d136-c44f-4d88-a347-fb7cf990046b/artifacts/urc7n4b4_ChatGPT%20Image%20Jan%2028%2C%202026%2C%2010_47_01%20PM.png",
    color: "#3B82F6",
    capabilities: ["Cinematic Ads", "AI Scripts", "Video production", "Brand storytelling", "Commercial quality"],
  },
];

const heroTeamVideo = "https://customer-assets.emergentagent.com/job_1311d09a-531f-4860-b5ab-e067dc11b06d/artifacts/392dlkkk_kling_20260205_Image_to_Video_Epic_cinem_123_0.mp4";

// Group Hero Image
const heroGroupImage = "https://customer-assets.emergentagent.com/job_aisearchhub-1/artifacts/phhh0xgx_ChatGPT%20Image%20Feb%204%2C%202026%2C%2004_10_58%20PM.png";

// Hero Categories
const heroCategories = [
  { icon: Megaphone, name: "Marketing & Traffic", count: "10+ Heroes" },
  { icon: Phone, name: "Communications", count: "7 Heroes" },
  { icon: TrendingUp, name: "Leads & Sales", count: "9 Heroes" },
  { icon: CreditCard, name: "Payments & Finance", count: "8 Heroes" },
  { icon: Building2, name: "Operations", count: "8 Heroes" },
  { icon: Globe, name: "Directory & Network", count: "8 Heroes" },
  { icon: Star, name: "Reputation & Community", count: "6 Heroes" },
  { icon: BarChart3, name: "Data & Reporting", count: "7 Heroes" },
  { icon: Users, name: "Human Capital", count: "5 Heroes" },
  { icon: Bot, name: "CRM Core", count: "9 Heroes" },
];

export default function HowItWorksPage() {
  const [categories, setCategories] = useState([]);
  const [featuredBusinesses, setFeaturedBusinesses] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [content, setContent] = useState([]);
  const [stats, setStats] = useState({ businesses: 0, ai_agents: 0, categories: 0 });
  const [currentHeroIndex, setCurrentHeroIndex] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catRes, bizRes, testRes, contentRes, statsRes] = await Promise.all([
          axios.get(`${API}/categories`),
          axios.get(`${API}/businesses/featured`),
          axios.get(`${API}/testimonials`),
          axios.get(`${API}/content`),
          axios.get(`${API}/stats`),
        ]);
        setCategories(catRes.data.slice(0, 8));
        setFeaturedBusinesses(bizRes.data.slice(0, 4));
        setTestimonials(testRes.data);
        setContent(contentRes.data.slice(0, 4));
        setStats(statsRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  // Auto-rotate hero slider
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentHeroIndex((prev) => (prev + 1) % aiBusinessHeroes.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const nextHero = () => {
    setCurrentHeroIndex((prev) => (prev + 1) % aiBusinessHeroes.length);
  };

  const prevHero = () => {
    setCurrentHeroIndex((prev) => (prev - 1 + aiBusinessHeroes.length) % aiBusinessHeroes.length);
  };

  const whatYouGet = [
    { icon: Award, title: "Best of Best Directory Listing", desc: "Premium visibility" },
    { icon: Bot, title: "Full Team of AI Business Heroes™", desc: "24/7 digital workforce" },
    { icon: Zap, title: "LocalBest.ai Platform", desc: "Complete business system" },
    { icon: Users, title: "AI Horse-Powered CRM", desc: "Smart customer management" },
    { icon: Building2, title: "BDC-X Business Dev Center", desc: "Lead generation engine" },
    { icon: DollarSign, title: "Up to $120,000 Co-Op Funding", desc: "Growth capital" },
  ];

  return (
    <div className="page-container">
      {/* Hero Section */}
      <section className="relative min-h-[70vh] lg:min-h-[85vh] flex items-center hero-pattern overflow-visible">
        {/* Background Glow */}
        <div className="hero-glow top-1/4 left-1/4 opacity-60" />
        <div className="hero-glow bottom-1/4 right-1/4 opacity-40" style={{ background: "radial-gradient(circle, rgba(0, 229, 255, 0.1) 0%, transparent 70%)" }} />
        
        {/* Grid Pattern */}
        <div className="absolute inset-0 grid-pattern opacity-30" />
        
        {/* Mobile Hero Background Image - Faded/Darkened */}
        <div className="absolute inset-0 lg:hidden">
          <img 
            src="https://customer-assets.emergentagent.com/job_aisearchhub-1/artifacts/phhh0xgx_ChatGPT%20Image%20Feb%204%2C%202026%2C%2004_10_58%20PM.png"
            alt=""
            className="w-full h-full object-cover object-top opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#020817]/60 via-[#020817]/70 to-[#020817]" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-8 lg:py-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F59E0B]/10 border border-[#F59E0B]/30 mb-4">
                <Sparkles className="w-4 h-4 text-[#F59E0B]" />
                <span className="text-sm text-[#F59E0B] font-medium">
                  Meet Your AI Business Heroes™ Team
                </span>
              </div>

              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-['Outfit'] leading-tight mb-4">
                <span className="text-white">One Listing.</span>
                <span className="gradient-text"> More Visibility.</span>
                <br />
                <span className="text-white text-lg sm:text-xl lg:text-2xl">AI Horse-Power™ <span className="text-[#10B981]">Working Around the Clock.</span></span>
              </h1>

              <p className="text-base text-[#94A3B8] mb-4 max-w-xl leading-relaxed">
                When your business joins the Best of Best Directory, you don&apos;t just get listed — 
                you get a <span className="text-[#10B981]">local-search-optimized business profile</span> and your own 
                <span className="text-[#00E5FF]"> AI Directory Hero</span> (Trained AI Agent), built to answer questions, 
                capture leads 24/7, and support your advertising across channels.
              </p>

              <p className="text-sm text-[#94A3B8] mb-4 max-w-xl leading-relaxed hidden lg:block">
                Every listing includes <span className="text-[#F59E0B]">built-in retargeting pixels</span> for Meta, Google, TV, Radio, 
                Postcards, and more — so one visit can trigger ongoing visibility for your brand across multiple platforms automatically.
              </p>

              {/* Hero Categories Preview - Hidden on mobile */}
              <div className="hidden lg:flex flex-wrap gap-2 mb-4">
                {["Marketing", "Sales", "Appointments", "Automations", "Billing", "CRM"].map((cat) => (
                  <span key={cat} className="px-3 py-1 text-xs rounded-full bg-white/5 border border-white/10 text-[#94A3B8]">
                    {cat} Hero
                  </span>
                ))}
                <span className="px-3 py-1 text-xs rounded-full bg-[#7C3AED]/20 border border-[#7C3AED]/30 text-[#7C3AED]">
                  +50 More Heroes
                </span>
              </div>

              <div className="flex flex-wrap gap-4">
                <a href="https://aihpower.com/best-of-best-listing" target="_blank" rel="noopener noreferrer">
                  <Button
                    className="btn-primary flex items-center gap-2"
                    data-testid="get-funded-btn"
                  >
                    Get $120K Funding
                    <DollarSign className="w-5 h-5" />
                  </Button>
                </a>
                <Link to="/ai-heroes">
                  <Button
                    variant="outline"
                    className="btn-secondary"
                    data-testid="meet-heroes-btn"
                  >
                    Meet The Heroes
                  </Button>
                </Link>
              </div>

              {/* Stats - Responsive */}
              <div className="flex items-center gap-4 lg:gap-8 mt-6 lg:mt-8">
                <div className="p-3 lg:p-4 rounded-xl bg-gradient-to-r from-[#10B981]/10 to-[#00E5FF]/10 border border-[#10B981]/20">
                  <p className="text-xl lg:text-3xl font-bold text-white font-['Outfit']">{stats.businesses > 0 ? stats.businesses.toLocaleString() + '+' : '33,000+'}</p>
                  <p className="text-xs lg:text-sm text-[#10B981]">AI Business Heroes™</p>
                </div>
                <div className="w-px h-10 lg:h-12 bg-white/10" />
                <div>
                  <p className="text-xl lg:text-3xl font-bold text-white font-['Outfit']">10</p>
                  <p className="text-xs lg:text-sm text-[#94A3B8]">Categories</p>
                </div>
                <div className="w-px h-10 lg:h-12 bg-white/10" />
                <div>
                  <p className="text-xl lg:text-3xl font-bold text-white font-['Outfit']">$120K</p>
                  <p className="text-xs lg:text-sm text-[#94A3B8]">Funding</p>
                </div>
              </div>
            </motion.div>

            {/* Right Content - AI Heroes Team Image */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative hidden lg:block"
            >
              <div className="relative">
                {/* Glow behind image */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#7C3AED]/30 to-[#00E5FF]/30 blur-3xl rounded-full scale-110" />
                
                {/* Team Video */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.3 }}
                  className="relative"
                >
                  <video
                    src={heroTeamVideo}
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="auto"
                    poster="https://customer-assets.emergentagent.com/job_1311d09a-531f-4860-b5ab-e067dc11b06d/artifacts/20kwvbqs_ChatGPT%20Image%20Feb%203%2C%202026%2C%2002_18_35%20PM.png"
                    className="w-full rounded-2xl shadow-2xl cursor-pointer"
                    style={{ minHeight: '400px', backgroundColor: '#1a1a2e' }}
                    onClick={(e) => {
                      // Toggle mute when user clicks
                      e.target.muted = !e.target.muted;
                    }}
                    title="Click to toggle sound"
                  />
                  
                  {/* Badge overlay */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8 }}
                  >
                  </motion.div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* === HOW IT WORKS — 6-Step Flow === */}
      <section className="relative py-20 md:py-28 bg-gradient-to-b from-transparent via-[#7C3AED]/5 to-transparent" data-testid="how-it-works-steps-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <p className="text-xs uppercase tracking-[0.2em] text-[#00E5FF] font-medium mb-3">The 6-Step Journey</p>
            <h2 className="text-3xl md:text-5xl font-bold text-white font-['Outfit'] leading-tight mb-4">
              From Listing to Lead Engine — in 6 Steps
            </h2>
            <p className="text-base md:text-lg text-[#94A3B8] leading-relaxed">
              LocalBest.ai is not just a directory. It's an AI-powered Best of Best Listing Funnel designed to help local businesses get discovered, build trust, capture leads, promote offers, and stay visible everywhere their customers are looking.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { n: "01", icon: FileText, title: "Create or Claim Your Listing", body: "Enter your basic info and website. We'll auto-match your listing to your brand using your website's logo, colors, images, and details." },
              { n: "02", icon: Sparkles, title: "LocalBest Builds Your Branded Listing", body: "Logo, brand colors, photos, About Us, FAQs, services, and offers populate automatically. You'll be live with a polished Best of Best presence — fast." },
              { n: "03", icon: Rocket, title: "Your Best of Best Funnel Goes Live", body: "Each listing comes with AI chat, rewards & offers, Call-Now CTAs, video, services, FAQs, contact info, map, and lead capture — built to convert." },
              { n: "04", icon: MessageSquare, title: "Visitors Engage + Become Leads", body: "Visitors ask your AI Business Hero, claim offers, call, request help, watch your video, view services, and submit lead forms — every action is tracked." },
              { n: "05", icon: BarChart3, title: "Leads Land in Your Dashboard", body: "Log in to see leads, lead source, AI chat transcripts, analytics, publishing tools, content, and listing activity — all in one place." },
              { n: "06", icon: Megaphone, title: "We Help Promote Your Listing", body: "On upgraded plans we push your funnel through social, search, retargeting, video, and paid ads — designed to keep your business visible everywhere." },
            ].map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.04 }}
                className="glass-card p-6 rounded-2xl border border-white/10 hover:border-[#00E5FF]/30 transition-all"
                data-testid={`how-it-works-step-${i + 1}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#7C3AED]/30 to-[#00E5FF]/10 border border-white/10 flex items-center justify-center">
                    <s.icon className="w-5 h-5 text-[#00E5FF]" />
                  </div>
                  <span className="font-mono text-2xl font-bold text-white/15">{s.n}</span>
                </div>
                <h3 className="text-white font-bold font-['Outfit'] text-lg mb-2">{s.title}</h3>
                <p className="text-sm text-[#94A3B8] leading-relaxed">{s.body}</p>
              </motion.div>
            ))}
          </div>

          {/* INSTANT BRAND MATCH callout */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 glass-card p-6 md:p-8 rounded-3xl border border-[#7C3AED]/30 bg-gradient-to-br from-[#7C3AED]/10 via-transparent to-[#00E5FF]/10"
            data-testid="instant-brand-match-callout"
          >
            <div className="flex flex-wrap items-start gap-4">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#7C3AED] to-[#00E5FF] flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-white font-bold font-['Outfit'] text-xl md:text-2xl mb-2">
                  Your Listing Starts Branded From Day One
                </h3>
                <p className="text-[#94A3B8] leading-relaxed mb-4 max-w-3xl">
                  No more blank profiles or generic directory pages. When you add your website, LocalBest.ai instantly pulls in your real logo, brand colors, business images, and website details to help build a polished Best of Best Listing that feels like an extension of your business.
                </p>
                <Link to="/submit-business">
                  <Button className="bg-gradient-to-r from-[#7C3AED] to-[#00E5FF] hover:from-[#00E5FF] hover:to-[#7C3AED] text-white font-semibold" data-testid="instant-brand-match-cta">
                    <Sparkles className="w-4 h-4 mr-2" /> Instantly Brand My Listing
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* === FULL BENEFITS SECTION (shared) === */}
      <ListingBenefitsSection />

      {/* Hero Categories Overview */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] text-white mb-4">
              An AI Business Hero™ for <span className="gradient-text">Every Category</span>
            </h2>
            <p className="text-[#94A3B8] text-lg max-w-2xl mx-auto">
              50+ specialized AI Heroes across 10 categories, all working together for your business
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4"
          >
            {heroCategories.map((cat, idx) => (
              <motion.div
                key={idx}
                variants={itemVariants}
                className="glass-card p-4 rounded-xl text-center group hover:border-[#7C3AED]/50 transition-colors"
              >
                <div className="w-12 h-12 rounded-xl bg-[#7C3AED]/20 flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform">
                  <cat.icon className="w-6 h-6 text-[#7C3AED]" />
                </div>
                <h4 className="text-white font-medium text-sm mb-1">{cat.name}</h4>
                <p className="text-[#00E5FF] text-xs">{cat.count}</p>
              </motion.div>
            ))}
          </motion.div>

          <div className="text-center mt-10">
            <Link to="/ai-heroes">
              <Button className="btn-primary">
                Meet All 50+ AI Business Heroes <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Hero Video Section */}
      <section className="py-12 md:py-20 bg-gradient-to-b from-[#020817] to-[#0a0f1e]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative rounded-2xl overflow-hidden neon-glow-purple bg-[#0a0f1e]"
          >
            <video
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              className="w-full h-auto object-cover"
            >
              <source 
                src="https://customer-assets.emergentagent.com/job_aisearchhub-1/artifacts/vgkl626y_kling_20260205_Image_to_Video_Epic_cinem_1596_0.mp4" 
                type="video/mp4" 
              />
              Your browser does not support the video tag.
            </video>
            {/* Gradient overlay at bottom */}
            <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-[#0a0f1e] to-transparent" />
          </motion.div>
        </div>
      </section>

      {/* SEO Benefits Section */}
      <section className="py-20 md:py-32 bg-gradient-to-b from-[#0a0f1e] to-[#020817]" data-testid="seo-benefits-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#10B981]/10 border border-[#10B981]/20 mb-6">
              <Search className="w-4 h-4 text-[#10B981]" />
              <span className="text-sm text-[#10B981]">SEO Power</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-bold font-['Outfit'] text-white mb-4">
              Why Your Listing <span className="gradient-text">Boosts Your SEO</span>
            </h2>
            <p className="text-[#94A3B8] text-lg max-w-3xl mx-auto">
              When you list your business in our LocalBest.ai directory, you gain a powerful SEO asset designed to help your business rank higher in local search and be found by both customers and AI search engines.
            </p>
          </motion.div>

          {/* SEO Benefits Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {/* Built for Google and AI Search */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="glass-card p-6 rounded-2xl"
            >
              <div className="w-12 h-12 rounded-xl bg-[#7C3AED]/20 flex items-center justify-center mb-4">
                <Brain className="w-6 h-6 text-[#7C3AED]" />
              </div>
              <h3 className="text-white font-semibold font-['Outfit'] text-lg mb-3">
                Built for Google & AI Search
              </h3>
              <p className="text-[#94A3B8] text-sm mb-4">
                Every listing is structured so Google, Bing, Apple Maps, ChatGPT, and voice assistants can understand and recommend your business.
              </p>
              <div className="space-y-2 text-sm text-[#E2E8F0]">
                <p className="italic">&quot;Best [service] near me&quot;</p>
                <p className="italic">&quot;Top [industry] in [city]&quot;</p>
                <p className="italic">&quot;Who does [service] locally?&quot;</p>
              </div>
            </motion.div>

            {/* Strong Local SEO Signals */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="glass-card p-6 rounded-2xl"
            >
              <div className="w-12 h-12 rounded-xl bg-[#00E5FF]/20 flex items-center justify-center mb-4">
                <MapPin className="w-6 h-6 text-[#00E5FF]" />
              </div>
              <h3 className="text-white font-semibold font-['Outfit'] text-lg mb-3">
                Strong Local SEO Signals (NAP)
              </h3>
              <p className="text-[#94A3B8] text-sm mb-4">
                Your listing includes Business Name, Address, Phone, Website, Services, City & State — all formatted with structured data.
              </p>
              <div className="space-y-2">
                {["Verifies your business is real", "Builds trust with Google", "Improves local map rankings"].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-[#E2E8F0] text-sm">
                    <CheckCircle2 className="w-4 h-4 text-[#00E5FF]" />
                    {item}
                  </div>
                ))}
              </div>
            </motion.div>

            {/* High-Quality Backlink */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="glass-card p-6 rounded-2xl"
            >
              <div className="w-12 h-12 rounded-xl bg-[#F59E0B]/20 flex items-center justify-center mb-4">
                <Link2 className="w-6 h-6 text-[#F59E0B]" />
              </div>
              <h3 className="text-white font-semibold font-['Outfit'] text-lg mb-3">
                High-Quality Backlink
              </h3>
              <p className="text-[#94A3B8] text-sm mb-4">
                Your listing includes a direct link to your website from a niche-relevant, location-based directory with SEO-friendly URLs.
              </p>
              <div className="space-y-2">
                {["Increases domain authority", "Helps website rank higher", "Drives referral traffic"].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-[#E2E8F0] text-sm">
                    <CheckCircle2 className="w-4 h-4 text-[#F59E0B]" />
                    {item}
                  </div>
                ))}
              </div>
            </motion.div>

            {/* More Places to Be Found */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="glass-card p-6 rounded-2xl"
            >
              <div className="w-12 h-12 rounded-xl bg-[#10B981]/20 flex items-center justify-center mb-4">
                <Eye className="w-6 h-6 text-[#10B981]" />
              </div>
              <h3 className="text-white font-semibold font-['Outfit'] text-lg mb-3">
                More Places to Be Found
              </h3>
              <p className="text-[#94A3B8] text-sm mb-4">
                Even if your website is not ranking yet, your directory listing can show up in search results.
              </p>
              <div className="space-y-2">
                {["Another digital property working for you", "More chances to be discovered", "No need to pay for ads"].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-[#E2E8F0] text-sm">
                    <CheckCircle2 className="w-4 h-4 text-[#10B981]" />
                    {item}
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Trust & Credibility */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="glass-card p-6 rounded-2xl"
            >
              <div className="w-12 h-12 rounded-xl bg-[#EC4899]/20 flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-[#EC4899]" />
              </div>
              <h3 className="text-white font-semibold font-['Outfit'] text-lg mb-3">
                Trust & Credibility
              </h3>
              <p className="text-[#94A3B8] text-sm mb-4">
                When people see your business listed in a verified directory, they trust you more — and trust converts into calls, clicks, and customers.
              </p>
              <div className="space-y-2">
                {["Online legitimacy", "Brand presence", "Local authority"].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-[#E2E8F0] text-sm">
                    <CheckCircle2 className="w-4 h-4 text-[#EC4899]" />
                    {item}
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Future of Search */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
              className="glass-card p-6 rounded-2xl"
            >
              <div className="w-12 h-12 rounded-xl bg-[#6366F1]/20 flex items-center justify-center mb-4">
                <Rocket className="w-6 h-6 text-[#6366F1]" />
              </div>
              <h3 className="text-white font-semibold font-['Outfit'] text-lg mb-3">
                Optimized for Future Search
              </h3>
              <p className="text-[#94A3B8] text-sm mb-4">
                Our directory is built for AI-powered search engines, voice search, local intent queries, and structured data (schema).
              </p>
              <p className="text-[#6366F1] text-sm font-medium">
                Your business is visible today and positioned for tomorrow.
              </p>
            </motion.div>
          </div>

          {/* AI Horse-Powered Advantage Box */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card p-8 md:p-12 rounded-2xl neon-glow-cyan text-center"
          >
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#00E5FF] flex items-center justify-center">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-2xl md:text-3xl font-bold font-['Outfit'] text-white">
                LocalBest.ai Advantage
              </h3>
            </div>
            <p className="text-[#94A3B8] text-lg mb-8 max-w-3xl mx-auto">
              Unlike basic directories, our listings are engineered for maximum SEO impact.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8 max-w-4xl mx-auto">
              {[
                "SEO-optimized",
                "AI-search friendly",
                "Local-map compatible",
                "Citation structured",
                "Backlink generating",
                "Long-term organic traffic"
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-2 text-[#E2E8F0] justify-center">
                  <CheckCircle2 className="w-5 h-5 text-[#00E5FF]" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
            <div className="bg-[#0a0f1e] rounded-xl p-6 max-w-3xl mx-auto">
              <p className="text-[#94A3B8] mb-4">Think of your listing as:</p>
              <div className="grid md:grid-cols-2 gap-4 text-left">
                <div className="flex items-start gap-3">
                  <TrendingUp className="w-5 h-5 text-[#10B981] mt-1" />
                  <span className="text-[#E2E8F0]">A permanent SEO boost</span>
                </div>
                <div className="flex items-start gap-3">
                  <Globe className="w-5 h-5 text-[#00E5FF] mt-1" />
                  <span className="text-[#E2E8F0]">A digital billboard in search results</span>
                </div>
                <div className="flex items-start gap-3">
                  <Brain className="w-5 h-5 text-[#7C3AED] mt-1" />
                  <span className="text-[#E2E8F0]">A trusted data source for AI</span>
                </div>
                <div className="flex items-start gap-3">
                  <ExternalLink className="w-5 h-5 text-[#F59E0B] mt-1" />
                  <span className="text-[#E2E8F0]">A second website working for you</span>
                </div>
              </div>
            </div>
            <div className="mt-8">
              <Link to="/directory">
                <Button className="btn-primary text-lg px-8 py-6">
                  Get Listed Now <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Retargeting Pixels Section with Video */}
      <section className="py-12 md:py-20 bg-gradient-to-b from-[#020817] via-[#0a0f1e] to-[#020817] relative overflow-hidden" data-testid="retargeting-section">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#F59E0B]/5 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#7C3AED]/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "1s" }} />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Video with Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-5xl mx-auto mb-8"
          >
            <div className="relative rounded-2xl overflow-hidden neon-glow-orange bg-[#0a0f1e]">
              <video
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
                className="w-full h-auto object-cover cursor-pointer"
                title="Click to toggle sound"
                onClick={(e) => {
                  e.target.muted = !e.target.muted;
                }}
              >
                <source 
                  src="https://customer-assets.emergentagent.com/job_b1b5869b-65cc-4b1f-ac0a-6c8ed691ccb8/artifacts/ce0ho5l9_kling_20260206_Image_to_Video_Epic_cinem_446_0.mp4" 
                  type="video/mp4" 
                />
                Your browser does not support the video tag.
              </video>
              {/* Badge attached to bottom of video */}
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-10">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F59E0B]/90 border border-[#F59E0B] backdrop-blur-sm shadow-lg">
                  <Target className="w-4 h-4 text-white" />
                  <span className="text-sm text-white font-medium">Multi-Channel Retargeting</span>
                </div>
              </div>
              {/* Gradient overlay at bottom */}
              <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[#0a0f1e] to-transparent" />
            </div>
          </motion.div>

          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-5xl font-bold font-['Outfit'] text-white mb-4">
              Turn One Visit Into <span className="text-[#F59E0B]">Everywhere Visibility</span>
            </h2>
            <p className="text-[#94A3B8] text-lg max-w-3xl mx-auto mb-6">
              Retargeting pixels are small pieces of tracking technology that remember when someone visits your business online —  
              so your brand can continue showing up after they leave.
            </p>
            <div className="flex flex-col md:flex-row items-center justify-center gap-4 text-[#E2E8F0]">
              <span className="text-sm">Most businesses rely on a single pixel on their website.</span>
              <span className="hidden md:block text-[#F59E0B]">→</span>
              <span className="text-sm font-semibold text-[#F59E0B]">LocalBest.ai listings go much further.</span>
            </div>
          </motion.div>

          {/* What Happens Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h3 className="text-2xl md:text-3xl font-bold font-['Outfit'] text-white mb-4">
              What Happens When Someone Views Your Listing?
            </h3>
            <p className="text-[#94A3B8] max-w-2xl mx-auto">
              A single visit to your listing can activate retargeting across:
            </p>
          </motion.div>

          {/* Retargeting Channels Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {/* Social Media */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="glass-card p-6 rounded-2xl border border-[#3b5998]/30 hover:border-[#3b5998]/60 transition-all group"
            >
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#3b5998] to-[#E1306C] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Smartphone className="w-7 h-7 text-white" />
              </div>
              <h4 className="text-white font-semibold font-['Outfit'] text-lg mb-2">Social Media (Meta)</h4>
              <p className="text-[#94A3B8] text-sm">
                Your ads follow them on Facebook, Instagram, Reels, Stories, and more while they scroll.
              </p>
            </motion.div>

            {/* Search & Display */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="glass-card p-6 rounded-2xl border border-[#4285F4]/30 hover:border-[#4285F4]/60 transition-all group"
            >
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#4285F4] to-[#34A853] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Monitor className="w-7 h-7 text-white" />
              </div>
              <h4 className="text-white font-semibold font-['Outfit'] text-lg mb-2">Search & Display (Google)</h4>
              <p className="text-[#94A3B8] text-sm">
                They see your brand again when they search online, browse websites, or watch YouTube.
              </p>
            </motion.div>

            {/* Premium TV */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="glass-card p-6 rounded-2xl border border-[#7C3AED]/30 hover:border-[#7C3AED]/60 transition-all group"
            >
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#7C3AED] to-[#EC4899] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Tv className="w-7 h-7 text-white" />
              </div>
              <h4 className="text-white font-semibold font-['Outfit'] text-lg mb-2">Premium TV Advertising</h4>
              <p className="text-[#94A3B8] text-sm">
                Your cinematic commercial appears across <span className="text-[#7C3AED] font-semibold">500+ premium TV stations</span> — on their couch, your brand shows up.
              </p>
            </motion.div>

            {/* Radio */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="glass-card p-6 rounded-2xl border border-[#F59E0B]/30 hover:border-[#F59E0B]/60 transition-all group"
            >
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#F59E0B] to-[#EF4444] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Volume2 className="w-7 h-7 text-white" />
              </div>
              <h4 className="text-white font-semibold font-['Outfit'] text-lg mb-2">Radio Advertising</h4>
              <p className="text-[#94A3B8] text-sm">
                Your message plays across <span className="text-[#F59E0B] font-semibold">1,000+ radio stations</span> — they hear your commercial while driving or working.
              </p>
            </motion.div>

            {/* Direct Mail */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="glass-card p-6 rounded-2xl border border-[#10B981]/30 hover:border-[#10B981]/60 transition-all group"
            >
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#10B981] to-[#059669] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Send className="w-7 h-7 text-white" />
              </div>
              <h4 className="text-white font-semibold font-['Outfit'] text-lg mb-2">First-Class Direct Mail</h4>
              <p className="text-[#94A3B8] text-sm">
                A professionally designed postcard delivered to their mailbox with QR tracking, offers, and specials.
              </p>
            </motion.div>

            {/* Email & Nurture */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
              className="glass-card p-6 rounded-2xl border border-[#00E5FF]/30 hover:border-[#00E5FF]/60 transition-all group"
            >
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#00E5FF] to-[#0EA5E9] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Mail className="w-7 h-7 text-white" />
              </div>
              <h4 className="text-white font-semibold font-['Outfit'] text-lg mb-2">Email & Nurture Campaigns</h4>
              <p className="text-[#94A3B8] text-sm">
                Prospects receive follow-up emails with coupons, content, reminders, and promotions.
              </p>
            </motion.div>

            {/* Content Retargeting */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6 }}
              className="glass-card p-6 rounded-2xl border border-[#EC4899]/30 hover:border-[#EC4899]/60 transition-all group"
            >
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#EC4899] to-[#F43F5E] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <FileText className="w-7 h-7 text-white" />
              </div>
              <h4 className="text-white font-semibold font-['Outfit'] text-lg mb-2">Content Retargeting</h4>
              <p className="text-[#94A3B8] text-sm">
                Blogs, pages, offers, services, or promotions continue retargeting the same audience.
              </p>
            </motion.div>

            {/* Additional Platforms */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.7 }}
              className="glass-card p-6 rounded-2xl border border-white/10 hover:border-white/30 transition-all group"
            >
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#6366F1] to-[#8B5CF6] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Globe className="w-7 h-7 text-white" />
              </div>
              <h4 className="text-white font-semibold font-['Outfit'] text-lg mb-2">Additional Platforms</h4>
              <p className="text-[#94A3B8] text-sm">
                TikTok, LinkedIn, X (Twitter), and more — expanding reach wherever prospects spend time.
              </p>
            </motion.div>
          </div>

          {/* Why This Matters */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card p-8 md:p-12 rounded-2xl border-2 border-[#F59E0B]/30 mb-12"
          >
            <div className="text-center mb-8">
              <h3 className="text-2xl md:text-3xl font-bold font-['Outfit'] text-white mb-4">
                Why This <span className="text-[#F59E0B]">Matters</span>
              </h3>
              <p className="text-[#94A3B8] text-lg max-w-2xl mx-auto">
                Most customers don&apos;t convert the first time they see you. But they do convert when they:
              </p>
            </div>
            <div className="grid md:grid-cols-4 gap-6 mb-8">
              {[
                { text: "See your brand repeatedly", icon: Eye },
                { text: "Recognize your name", icon: Star },
                { text: "Feel familiar and confident", icon: Shield },
                { text: "Believe you're established", icon: Award }
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="text-center"
                >
                  <div className="w-16 h-16 rounded-full bg-[#F59E0B]/10 border border-[#F59E0B]/30 flex items-center justify-center mx-auto mb-3">
                    <item.icon className="w-8 h-8 text-[#F59E0B]" />
                  </div>
                  <p className="text-[#E2E8F0] font-medium">{item.text}</p>
                </motion.div>
              ))}
            </div>
            <div className="text-center">
              <p className="text-xl text-[#10B981] font-semibold mb-2">
                Retargeting turns interest into memory, and memory into action.
              </p>
              <p className="text-[#94A3B8]">
                Your listing doesn&apos;t just get seen — it keeps working long after the visit ends.
              </p>
            </div>
          </motion.div>

          {/* Why Different */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card p-8 md:p-12 rounded-2xl neon-glow-orange text-center"
          >
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-gradient-to-r from-[#F59E0B] to-[#EF4444] flex items-center justify-center">
                <Repeat className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-2xl md:text-3xl font-bold font-['Outfit'] text-white">
                Why This Is Different
              </h3>
            </div>
            <div className="flex flex-col md:flex-row items-center justify-center gap-8 mb-8">
              <div className="text-center">
                <p className="text-[#94A3B8] text-lg mb-2">Most directories stop at</p>
                <p className="text-2xl font-bold text-white">Exposure</p>
              </div>
              <div className="text-4xl text-[#F59E0B]">→</div>
              <div className="text-center">
                <p className="text-[#94A3B8] text-lg mb-2">LocalBest.ai listings activate</p>
                <p className="text-2xl font-bold text-[#F59E0B]">Visibility</p>
              </div>
            </div>
            <div className="grid md:grid-cols-4 gap-4 mb-8 max-w-4xl mx-auto">
              {[
                "Follows them digitally",
                "Reappears on screens & speakers",
                "Shows up in their mailbox",
                "Reinforces your brand automatically"
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-2 text-[#E2E8F0] justify-center">
                  <CheckCircle2 className="w-5 h-5 text-[#F59E0B]" />
                  <span className="text-sm">{item}</span>
                </div>
              ))}
            </div>
            <div className="bg-[#0a0f1e] rounded-xl p-8 max-w-2xl mx-auto">
              <p className="text-[#94A3B8] text-lg mb-4">In Simple Terms:</p>
              <p className="text-2xl md:text-3xl font-bold text-white mb-2">
                They see your listing once —
              </p>
              <p className="text-2xl md:text-3xl font-bold text-[#F59E0B]">
                then they see your ads everywhere.
              </p>
              <p className="text-xl text-[#10B981] mt-4 font-semibold">
                That&apos;s AI Horse-Power.
              </p>
            </div>
            <div className="mt-8">
              <Link to="/directory">
                <Button className="bg-gradient-to-r from-[#F59E0B] to-[#EF4444] hover:opacity-90 text-white text-lg px-8 py-6">
                  Get Your Retargeting-Ready Listing <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Managed Ad Heroes Section - Managed Services with Co-Op Funding */}
      <section className="py-20 md:py-32 bg-gradient-to-b from-[#7C3AED]/5 to-transparent" data-testid="pod-ads-heroes-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F59E0B]/10 border border-[#F59E0B]/20 mb-6">
              <Megaphone className="w-4 h-4 text-[#F59E0B]" />
              <span className="text-sm text-[#F59E0B]">Managed Ad Services</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] text-white mb-4">
              Meet Your <span className="text-[#F59E0B]">AI Horse-Powered Ad Heroes</span>
            </h2>
            <p className="text-[#94A3B8] text-lg max-w-3xl mx-auto">
              Get your ads ordered and launched with our managed services — <span className="text-[#10B981] font-semibold">covered by Co-Op funding</span>. 
              These heroes handle everything from creative to campaigns.
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-4 gap-6"
          >
            {podAdsHeroes.map((hero, idx) => (
              <motion.div
                key={hero.id}
                variants={itemVariants}
                className="glass-card rounded-2xl overflow-hidden group hover:border-[#F59E0B]/50 transition-all"
              >
                <div className="relative aspect-square overflow-hidden">
                  <img
                    src={hero.image}
                    alt={hero.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#020817] via-transparent to-transparent" />
                  <div className="absolute bottom-3 left-3 right-3">
                    <span 
                      className="px-2 py-1 rounded-full text-xs font-medium"
                      style={{ backgroundColor: `${hero.color}30`, color: hero.color }}
                    >
                      {hero.role.replace('AI ', '').replace(' Hero', '')}
                    </span>
                  </div>
                </div>
                <div className="p-4">
                  <h4 className="text-white font-bold font-['Outfit'] mb-1">{hero.name}</h4>
                  <p className="text-[#94A3B8] text-xs line-clamp-2 mb-3">{hero.description}</p>
                  <div className="flex flex-wrap gap-1">
                    {hero.capabilities.slice(0, 3).map((cap, i) => (
                      <span key={i} className="px-2 py-0.5 rounded-full bg-white/5 text-[#94A3B8] text-[10px]">
                        {cap}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 text-center"
          >
            <div className="glass-card inline-flex items-center gap-4 px-6 py-4 rounded-2xl">
              <div className="flex -space-x-3">
                {podAdsHeroes.slice(0, 4).map((hero) => (
                  <img
                    key={hero.id}
                    src={hero.image}
                    alt={hero.name}
                    className="w-10 h-10 rounded-full border-2 border-[#020817] object-cover"
                  />
                ))}
              </div>
              <div className="text-left">
                <p className="text-white font-semibold">8 Ad Heroes Ready</p>
                <p className="text-[#10B981] text-sm">Covered by Co-Op Funding</p>
              </div>
              <a href="https://aihpower.com/best-of-best-listing" target="_blank" rel="noopener noreferrer">
                <Button className="btn-primary">
                  Get Funded <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What You Get Section */}
      <section className="py-20 md:py-32 bg-white/[0.01]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-bold font-['Outfit'] text-white mb-4">
              What You Get as a <span className="gradient-text">Best of Best</span> Business
            </h2>
            <p className="text-[#94A3B8] text-lg max-w-2xl mx-auto">
              You don't just get listed. You get a complete AI-powered business growth system.
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12"
          >
            {whatYouGet.map((item, idx) => (
              <motion.div
                key={idx}
                variants={itemVariants}
                className="glass-card p-6 rounded-2xl group hover:border-[#7C3AED]/50 transition-colors"
              >
                <div className="w-12 h-12 rounded-xl bg-[#7C3AED]/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <item.icon className="w-6 h-6 text-[#7C3AED]" />
                </div>
                <h3 className="text-white font-semibold font-['Outfit'] text-lg mb-2">
                  {item.title}
                </h3>
                <p className="text-[#94A3B8] text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>

          {/* Promotion Channels */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card p-8 rounded-2xl text-center"
          >
            <h3 className="text-white font-semibold font-['Outfit'] text-xl mb-6">
              Your Business Gets Promoted Across:
            </h3>
            <div className="flex flex-wrap justify-center gap-4">
              {[
                { icon: Globe, label: "Directory" },
                { icon: Tv, label: "TV" },
                { icon: Radio, label: "Radio" },
                { icon: Megaphone, label: "Meta Ads" },
                { icon: TrendingUp, label: "Google Ads" },
                { icon: Play, label: "TikTok" },
                { icon: Users, label: "Social" },
              ].map((channel, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10"
                >
                  <channel.icon className="w-4 h-4 text-[#00E5FF]" />
                  <span className="text-white text-sm">{channel.label}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Live Content Strip */}
      {content.length > 0 && (
        <section className="py-6 bg-white/[0.02] border-y border-white/5 overflow-hidden">
          <div className="flex items-center animate-marquee">
            {[...content, ...content].map((item, idx) => (
              <div
                key={idx}
                className="flex items-center gap-4 px-8 whitespace-nowrap"
              >
                {item.is_live && (
                  <span className="flex items-center gap-2 text-red-400 text-sm">
                    <span className="live-indicator" /> LIVE
                  </span>
                )}
                <span className="text-[#94A3B8] text-sm">{item.title}</span>
                <span className="text-white/20">•</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Categories Section */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] text-white mb-4">
              Explore Business Categories
            </h2>
            <p className="text-[#94A3B8] text-lg max-w-2xl mx-auto">
              Find the best businesses across industries, all powered by AI Business Heroes™
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
          >
            {categories.map((category) => (
              <motion.div key={category.id} variants={itemVariants}>
                <Link
                  to={`/directory/${category.slug}`}
                  className="group block glass-card p-6 rounded-2xl category-card relative overflow-hidden"
                  data-testid={`category-${category.slug}`}
                >
                  <div
                    className="category-bg"
                    style={{ backgroundImage: `url(${category.image_url})` }}
                  />
                  <div className="relative z-10">
                    <span className="text-3xl mb-3 block">{category.icon}</span>
                    <h3 className="text-white font-semibold font-['Outfit'] text-lg mb-1">
                      {category.name}
                    </h3>
                    <p className="text-[#94A3B8] text-sm">{category.description}</p>
                    <div className="mt-4 flex items-center gap-2 text-[#7C3AED] text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                      View Businesses <ChevronRight className="w-4 h-4" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>

          <div className="text-center mt-10">
            <Link to="/directory">
              <Button variant="outline" className="btn-secondary">
                View All Categories <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Businesses */}
      <section className="py-20 md:py-32 bg-white/[0.01]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16"
          >
            <div>
              <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] text-white mb-4">
                Featured Businesses
              </h2>
              <p className="text-[#94A3B8] text-lg max-w-xl">
                Top-rated businesses with their own AI Business Heroes™ working 24/7
              </p>
            </div>
            <Link to="/directory?featured=true">
              <Button variant="outline" className="btn-secondary">
                View All <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {featuredBusinesses.map((business) => (
              <motion.div key={business.id} variants={itemVariants}>
                <Link
                  to={`/business/${business.slug}`}
                  className="block glass-card rounded-2xl overflow-hidden card-lift"
                  data-testid={`business-${business.slug}`}
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <img
                        src={business.logo_url}
                        alt={business.name}
                        className="w-14 h-14 rounded-xl object-cover bg-white/5"
                      />
                      {business.is_best_of_best && (
                        <span className="best-badge">
                          <Award className="w-3 h-3" /> Best
                        </span>
                      )}
                    </div>
                    <h3 className="text-white font-semibold font-['Outfit'] text-lg mb-1">
                      {business.name}
                    </h3>
                    <p className="text-[#7C3AED] text-sm mb-3">
                      {business.category_name}
                    </p>
                    <p className="text-[#94A3B8] text-sm line-clamp-2">
                      {business.short_description}
                    </p>
                    <div className="flex items-center gap-1 mt-4 text-[#F59E0B]">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-current" />
                      ))}
                      <span className="text-[#94A3B8] text-sm ml-2">
                        ({business.review_count} reviews)
                      </span>
                    </div>
                  </div>
                  <div className="px-6 py-4 bg-white/[0.02] border-t border-white/5 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <img
                        src={business.ai_agent_avatar}
                        alt={business.ai_agent_name}
                        className="w-6 h-6 rounded-full"
                      />
                      <span className="text-[#94A3B8] text-sm">
                        {business.ai_agent_name} • AI Hero™
                      </span>
                    </div>
                    <Bot className="w-4 h-4 text-[#00E5FF]" />
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Why This Matters Section */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] text-white mb-6">
                Why This <span className="gradient-text">Matters</span>
              </h2>
              <p className="text-[#94A3B8] text-lg mb-8">
                Most businesses miss calls, lose leads, forget follow-ups, 
                don't market consistently, and don't track performance.
              </p>
              <div className="space-y-4 mb-8">
                {[
                  "Your AI Business Heroes™ never sleep",
                  "Works 24/7 promoting your business",
                  "Selling and booking automatically",
                  "Managing and reporting constantly",
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#00E5FF]/20 flex items-center justify-center">
                      <CheckCircle2 className="w-4 h-4 text-[#00E5FF]" />
                    </div>
                    <span className="text-white">{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-xl text-white font-semibold font-['Outfit']">
                So you can focus on running your business.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="glass-card p-8 rounded-2xl neon-glow-purple"
            >
              <h3 className="text-2xl font-bold font-['Outfit'] text-white mb-6">
                Ready to Get Your AI Business Hero™ Team?
              </h3>
              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3">
                  <DollarSign className="w-6 h-6 text-[#F59E0B]" />
                  <span className="text-white">Up to $120,000 in Co-Op Funding</span>
                </div>
                <div className="flex items-center gap-3">
                  <Bot className="w-6 h-6 text-[#7C3AED]" />
                  <span className="text-white">Full Team of AI Business Heroes™</span>
                </div>
                <div className="flex items-center gap-3">
                  <Zap className="w-6 h-6 text-[#00E5FF]" />
                  <span className="text-white">Complete Platform & CRM System</span>
                </div>
              </div>
              <div className="space-y-3">
                <a href="https://aihpower.com/best-of-best-listing" target="_blank" rel="noopener noreferrer" className="block">
                  <Button className="w-full btn-primary py-6 text-lg">
                    Get $120K Funding <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </a>
                <a href="https://localbest.ai" target="_blank" rel="noopener noreferrer" className="block">
                  <Button variant="outline" className="w-full btn-secondary py-6">
                    Visit LocalBest.ai
                  </Button>
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      {testimonials.length > 0 && (
        <section className="py-20 md:py-32 bg-white/[0.01]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] text-white mb-4">
                Trusted by Businesses
              </h2>
              <p className="text-[#94A3B8] text-lg">
                See what our community says about their AI Business Heroes™
              </p>
            </motion.div>

            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid md:grid-cols-3 gap-6"
            >
              {testimonials.map((testimonial) => (
                <motion.div
                  key={testimonial.id}
                  variants={itemVariants}
                  className="testimonial-card glass-card p-6 rounded-2xl"
                >
                  <div className="flex items-center gap-1 mb-4 text-[#F59E0B]">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-current" />
                    ))}
                  </div>
                  <p className="text-[#E2E8F0] mb-6 leading-relaxed">
                    "{testimonial.content}"
                  </p>
                  <div className="flex items-center gap-3">
                    <img
                      src={testimonial.author_avatar}
                      alt={testimonial.author_name}
                      className="w-10 h-10 rounded-full"
                    />
                    <div>
                      <p className="text-white font-medium text-sm">
                        {testimonial.author_name}
                      </p>
                      <p className="text-[#94A3B8] text-xs">
                        {testimonial.author_title}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>
      )}

      {/* Deep Dive Educational Section */}
      <section className="py-20 md:py-32 bg-gradient-to-b from-[#020817] to-[#0a0f1e]" data-testid="deep-dive-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#7C3AED]/10 border border-[#7C3AED]/20 mb-6">
              <Brain className="w-4 h-4 text-[#7C3AED]" />
              <span className="text-sm text-[#7C3AED]">Deep Dive</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-bold font-['Outfit'] text-white mb-4">
              How It All <span className="gradient-text">Works Together</span>
            </h2>
            <p className="text-[#94A3B8] text-lg max-w-3xl mx-auto">
              Understand the complete ecosystem that powers your business growth
            </p>
          </motion.div>

          {/* Group Hero Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative mb-16 max-w-4xl mx-auto"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-[#7C3AED]/30 to-[#00E5FF]/30 blur-3xl rounded-full" />
            <img
              src={heroGroupImage}
              alt="AI Horse-Powered Heroes Team"
              className="relative rounded-2xl shadow-2xl w-full border border-white/10"
            />
          </motion.div>

          {/* Educational Cards Grid */}
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {/* What is AI Horse-Powered */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="glass-card p-8 rounded-2xl"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#7C3AED]/20 flex items-center justify-center">
                  <Rocket className="w-6 h-6 text-[#7C3AED]" />
                </div>
                <h3 className="text-xl font-bold font-['Outfit'] text-white">
                  What is the AI Horse-Powered Platform?
                </h3>
              </div>
              <p className="text-[#94A3B8] mb-6">
                Think of our AI Horse-Powered Platform as your business's entire digital growth department — already built and ready to work. For more info visit our{" "}
                <a href="https://localbest.ai" target="_blank" rel="noopener noreferrer" className="text-[#00E5FF] hover:underline">AI Horse-Powered Website</a>.
              </p>
              <p className="text-white font-medium mb-4">Instead of hiring:</p>
              <div className="grid grid-cols-2 gap-2 mb-6">
                {["Marketing team", "Sales team", "Call center", "Content team", "Tech team", "CRM manager"].map((team, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-[#94A3B8] text-sm">
                    <X className="w-4 h-4 text-red-400" />
                    {team}
                  </div>
                ))}
              </div>
              <p className="text-[#00E5FF] font-medium mb-4">You get 40+ Virtual DFY Departments:</p>
              <div className="flex flex-wrap gap-2">
                {["Marketing", "Ads", "Leads", "Follow-up", "Appointments", "Billing", "Reviews", "Content", "Reporting", "Funding", "Operations"].map((dept, idx) => (
                  <span key={idx} className="px-3 py-1 rounded-full bg-[#00E5FF]/10 text-[#00E5FF] text-xs">
                    ✓ {dept}
                  </span>
                ))}
              </div>
            </motion.div>

            {/* What are AI Business Heroes */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="glass-card p-8 rounded-2xl"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#00E5FF]/20 flex items-center justify-center">
                  <Bot className="w-6 h-6 text-[#00E5FF]" />
                </div>
                <h3 className="text-xl font-bold font-['Outfit'] text-white">
                  What are AI Business Heroes™?
                </h3>
              </div>
              <p className="text-[#94A3B8] mb-6">
                Your AI Business Heroes™ are the AI Agents that run everything. Instead of "a bunch of software and automations", you get a full AI-powered business team working for you 24/7.
              </p>
              <p className="text-white font-medium mb-4">They are:</p>
              <div className="space-y-3 mb-6">
                {[
                  "Your digital spokesperson",
                  "Your sales assistant",
                  "Your marketing assistant",
                  "Your operations assistant",
                  "Your reporting assistant"
                ].map((role, idx) => (
                  <div key={idx} className="flex items-center gap-3 text-[#E2E8F0]">
                    <CheckCircle2 className="w-4 h-4 text-[#00E5FF]" />
                    {role}
                  </div>
                ))}
              </div>
              <p className="text-[#94A3B8] text-sm">
                They talk to your leads, book appointments, follow up, manage your pipeline, send proposals and invoices, publish content, and track results.
              </p>
            </motion.div>

            {/* What Powers the Heroes */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="glass-card p-8 rounded-2xl"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#F59E0B]/20 flex items-center justify-center">
                  <Settings className="w-6 h-6 text-[#F59E0B]" />
                </div>
                <h3 className="text-xl font-bold font-['Outfit'] text-white">
                  What Powers the AI Business Heroes?
                </h3>
              </div>
              <p className="text-[#94A3B8] mb-6">
                Your AI Business Heroes run on two powerful engines working together:
              </p>
              <div className="space-y-4">
                <div className="p-4 rounded-xl bg-[#7C3AED]/10 border border-[#7C3AED]/20">
                  <h4 className="text-[#7C3AED] font-semibold mb-2 flex items-center gap-2">
                    <Settings className="w-4 h-4" /> AI Horse-Powered Platform
                  </h4>
                  <p className="text-[#94A3B8] text-sm">
                    40+ Virtual DFY Departments: Marketing, Ads, Lead Management, Appointments, Content, Reviews, Reporting, Funding, Operations — they handle the actual work.
                  </p>
                </div>
                <div className="p-4 rounded-xl bg-[#00E5FF]/10 border border-[#00E5FF]/20">
                  <h4 className="text-[#00E5FF] font-semibold mb-2 flex items-center gap-2">
                    <BarChart3 className="w-4 h-4" /> AI Horse-Powered CRM
                  </h4>
                  <p className="text-[#94A3B8] text-sm">
                    The control center: Stores leads, tracks pipeline, syncs calendar, sends texts & emails, manages tasks, tracks payments, shows reports.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* What is Best of Best Directory */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="glass-card p-8 rounded-2xl"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#10B981]/20 flex items-center justify-center">
                  <Globe className="w-6 h-6 text-[#10B981]" />
                </div>
                <h3 className="text-xl font-bold font-['Outfit'] text-white">
                  What is the Best of Best Directory?
                </h3>
              </div>
              <p className="text-[#94A3B8] mb-6">
                It's not just a listing site. When your business is featured, you get massive visibility and lead generation.
              </p>
              <p className="text-white font-medium mb-4">When featured, you get:</p>
              <div className="space-y-2 mb-4">
                {[
                  "Your own AI Business Hero",
                  "Promotion across Directory, TV, Radio, Social",
                  "Google, Meta, TikTok advertising",
                  "Leads routed into your CRM",
                  "AI Heroes take over from there"
                ].map((benefit, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-[#E2E8F0] text-sm">
                    <CheckCircle2 className="w-4 h-4 text-[#10B981]" />
                    {benefit}
                  </div>
                ))}
              </div>
              <p className="text-[#10B981] font-medium text-sm">
                This is what keeps your pipeline full.
              </p>
            </motion.div>
          </div>

          {/* $120K Funding Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card p-8 md:p-12 rounded-2xl neon-glow-gold mb-16"
          >
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-[#F59E0B]/20 flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-[#F59E0B]" />
                  </div>
                  <h3 className="text-2xl font-bold font-['Outfit'] text-white">
                    What is the $120,000 Co-Op Funding?
                  </h3>
                </div>
                <p className="text-[#94A3B8] mb-6">
                  Approved businesses receive up to <span className="text-[#F59E0B] font-bold">$120,000 in Co-Op Marketing & Technology Funding</span>. So instead of paying everything out of pocket, you use your Co-Op Funds to power growth.
                </p>
                <p className="text-white font-medium mb-4">This covers:</p>
                <div className="grid grid-cols-2 gap-2">
                  {["Marketing", "Ads", "Automations", "Managed services", "CRM", "AI Business Heroes", "Directory placement"].map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-[#E2E8F0] text-sm">
                      <CheckCircle2 className="w-4 h-4 text-[#F59E0B]" />
                      {item}
                    </div>
                  ))}
                </div>
              </div>
              <div className="text-center">
                <div className="text-6xl md:text-8xl font-bold font-['Outfit'] text-[#F59E0B] mb-4">
                  $120K
                </div>
                <p className="text-white text-xl font-semibold mb-6">Co-Op Funding Available</p>
                <a href="https://aihpower.com/best-of-best-listing" target="_blank" rel="noopener noreferrer">
                  <Button className="btn-primary text-lg px-8 py-6">
                    Apply for Funding <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </a>
              </div>
            </div>
          </motion.div>

          {/* Why Different + Limited Availability */}
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {/* Why Different */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="glass-card p-8 rounded-2xl"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#7C3AED]/20 flex items-center justify-center">
                  <Award className="w-6 h-6 text-[#7C3AED]" />
                </div>
                <h3 className="text-xl font-bold font-['Outfit'] text-white">
                  Why This is Different
                </h3>
              </div>
              <p className="text-[#94A3B8] mb-6">
                Most businesses rely on referrals, miss calls, forget follow-ups, don't market consistently, and guess what's working.
              </p>
              <p className="text-white font-semibold mb-4">With us, you get an unfair advantage:</p>
              <div className="space-y-2">
                {[
                  "AI Business Heroes",
                  "AI Horse-Powered Platform",
                  "AI Horse-Powered CRM",
                  "Best of Best Directory",
                  "Multi-channel promotion",
                  "Automated lead flow"
                ].map((adv, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-[#E2E8F0]">
                    <CheckCircle2 className="w-4 h-4 text-[#7C3AED]" />
                    {adv}
                  </div>
                ))}
              </div>
              <p className="text-[#00E5FF] mt-6 font-medium">
                You never have to wonder: "Where is my next customer coming from?"
              </p>
            </motion.div>

            {/* Limited Availability */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="glass-card p-8 rounded-2xl border-2 border-[#EF4444]/30"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#EF4444]/20 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-[#EF4444]" />
                </div>
                <h3 className="text-xl font-bold font-['Outfit'] text-white">
                  Limited Availability
                </h3>
              </div>
              <p className="text-[#EF4444] font-semibold text-lg mb-4">
                Why You Must Act Fast
              </p>
              <p className="text-[#94A3B8] mb-6">
                Only <span className="text-white font-semibold">1 business per category per marketplace</span> (1 per 100,000 population)
              </p>
              <p className="text-white font-medium mb-4">That means only one:</p>
              <div className="grid grid-cols-2 gap-2 mb-6">
                {["Roofer", "HVAC company", "Realtor", "Lender", "Attorney", "And more..."].map((biz, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-[#E2E8F0] text-sm">
                    <Lock className="w-4 h-4 text-[#EF4444]" />
                    {biz}
                  </div>
                ))}
              </div>
              <p className="text-[#EF4444] font-semibold">
                Once your category is taken... It's locked.
              </p>
            </motion.div>
          </div>

          {/* Simple Version Summary */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card p-8 md:p-12 rounded-2xl text-center neon-glow-cyan"
          >
            <Target className="w-16 h-16 text-[#00E5FF] mx-auto mb-6" />
            <h3 className="text-2xl md:text-3xl font-bold font-['Outfit'] text-white mb-6">
              Simple Version for Business Owners
            </h3>
            <p className="text-xl text-[#94A3B8] mb-8 max-w-3xl mx-auto">
              We build you an <span className="text-[#00E5FF]">AI-powered business machine</span> that gets you noticed, gets you leads, follows up, books appointments, manages operations, and shows results.
            </p>
            <p className="text-2xl text-white font-bold mb-8">
              Backed by up to <span className="text-[#F59E0B]">$120,000</span> in Co-Op Funding
            </p>
            <p className="text-lg text-[#00E5FF] font-medium mb-8">
              So you grow faster... With less risk... And less work.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="https://aihpower.com/best-of-best-listing" target="_blank" rel="noopener noreferrer">
                <Button className="btn-primary text-lg px-8 py-6">
                  Get $120K Funding <DollarSign className="w-5 h-5 ml-2" />
                </Button>
              </a>
              <a href="https://localbest.ai" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="btn-secondary text-lg px-8 py-6">
                  Visit LocalBest.ai <Zap className="w-5 h-5 ml-2" />
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 40+ Departments Section */}
      <section className="py-12 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex items-center justify-between mb-8"
          >
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Bot className="w-6 h-6 text-[#7C3AED]" />
                <h2 className="text-2xl font-bold font-['Outfit'] text-white">40+ Departments</h2>
              </div>
              <p className="text-[#94A3B8]">Vetted AI Certified Experts • Co-Op Pricing</p>
            </div>
            <a 
              href="https://localbest.ai" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hidden md:flex items-center gap-2 text-[#7C3AED] hover:text-[#8B5CF6] transition-colors"
            >
              View all <ExternalLink className="w-4 h-4" />
            </a>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-3"
          >
            {[
              { name: "AI Marketing", icon: Megaphone, certified: true },
              { name: "Social Media", icon: Users, certified: true },
              { name: "Content Writing", icon: FileText, certified: true },
              { name: "SEO Expert", icon: Search, certified: true },
              { name: "Email Marketing", icon: Mail, certified: true },
              { name: "Ads Management", icon: BarChart3, certified: true },
              { name: "Brand Design", icon: Palette, certified: true },
              { name: "Web Development", icon: Brain, certified: true },
              { name: "Video Production", icon: Play, certified: true },
              { name: "Photography", icon: Eye, certified: true },
              { name: "Podcast Host", icon: Megaphone, certified: true },
              { name: "Virtual Assistant", icon: MessageSquare, certified: true },
              { name: "CRM Setup", icon: Settings, certified: true },
              { name: "AI Automation", icon: Bot, certified: true },
              { name: "Sales Funnels", icon: BarChart3, certified: true },
              { name: "Copywriting", icon: FileText, certified: true },
            ].map((dept, idx) => (
              <motion.div
                key={idx}
                variants={itemVariants}
                className="glass-card p-4 rounded-xl text-center group hover:border-[#7C3AED]/50 transition-all"
              >
                <div className="w-10 h-10 rounded-lg bg-[#7C3AED]/20 flex items-center justify-center mx-auto mb-2 group-hover:scale-110 transition-transform">
                  <dept.icon className="w-5 h-5 text-[#7C3AED]" />
                </div>
                <span className="text-white text-xs font-medium block mb-1">{dept.name}</span>
                {dept.certified && (
                  <span className="text-[#00E5FF] text-[10px] flex items-center justify-center gap-1">
                    <Shield className="w-3 h-3" /> AI Certified
                  </span>
                )}
              </motion.div>
            ))}
          </motion.div>

          <div className="text-center mt-6 md:hidden">
            <a 
              href="https://localbest.ai" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <Button variant="outline" className="btn-secondary">
                View All 40+ Departments <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 relative overflow-hidden">
        <div className="hero-glow top-0 left-1/2 -translate-x-1/2" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-3xl md:text-5xl font-bold font-['Outfit'] text-white mb-6">
              Get Your <span className="gradient-text">AI Business Hero™</span> Team Today
            </h2>
            <p className="text-[#94A3B8] text-lg mb-10 max-w-2xl mx-auto">
              Join the Best of Best directory, get up to $120K in funding, and let your 
              AI Business Heroes™ work for you 24/7.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="https://aihpower.com/best-of-best-listing" target="_blank" rel="noopener noreferrer">
                <Button className="btn-primary text-lg px-10 py-6">
                  Get $120K Funding <DollarSign className="w-5 h-5 ml-2" />
                </Button>
              </a>
              <a href="https://localbest.ai" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="btn-secondary text-lg px-10 py-6">
                  Visit LocalBest.ai <Zap className="w-5 h-5 ml-2" />
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <BackgroundVideoStrip src={BG_VIDEOS.howItWorks} testid="howitworks-bg-video" />
    </div>
  );
}
